import '@Pages/GeoMMM/Results/Components/SnapshotChartWithCI.scss';
import {
  useMemo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  ReactNode,
  memo,
  CSSProperties
} from 'react';
import * as R from 'ramda';
import { BarChart, Bar, Cell, LabelList, ResponsiveContainer, ErrorBar, Tooltip } from 'recharts';
import { ChartXAxis, ChartYAxis } from '@Components/Charts/chart-presets';
import cn from 'classnames';
import { MdOutlineWarningAmber } from 'react-icons/md';
import ReactDOM from 'react-dom';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { ChartContainer } from '@Components/ChartContainer';
import { InteractiveDiv } from '@Components/InteractiveEl';
import {
  BAR_CATEGORY_GAP,
  DataItem,
  DEFAULT_BAR_CHART_CONTAINER_HEIGHT,
  DEFAULT_FORMATTER,
  TOOLTIP_MARGINS
} from '@Components/SnapshotChart/snapshot-chart-constants';
import { Placement } from '@Components/designConstants';
import { getColorMap, Brand100 } from '../../../../utils/colors';
import { downloadPNG, exportToExcel } from '../../../../utils/download-utils';

interface DropdownOption {
  value: string;
}

interface LegendProps {
  name: string;
  value: number;
  value2: number | undefined;
  computedValue?: (value: number, value2?: number) => string;
  formatter: (value: number) => string;
  incrementality?: boolean;
  expanded?: boolean;
  label?: number | string;
  label2?: number | string;
  actualValues?: boolean;
  onClick?: () => void;
}

export const Legend = ({
  name,
  value,
  value2,
  computedValue,
  label2,
  label,
  formatter,
  incrementality,
  expanded,
  actualValues = false,
  onClick
}: LegendProps) => {
  const legendClassName = cn('legend', { 'has-computed-value': Boolean(computedValue) });
  const legendContents = (
    <>
      <div className='item-label'>
        <div className='item-label-text'>
          {name}
          {incrementality && <MdOutlineWarningAmber className='warning-icon' />}
        </div>
      </div>
      {computedValue && (
        <div className='item-computed-value'>
          <div>{computedValue(value, value2)}</div>
        </div>
      )}
      {expanded ? (
        <div className='item-value-wrapper'>
          <div className='item-value'>
            <div className='item-name-and-percent'>
              <span>Spend:</span>
              <span className='legend-percent-span'>{formatter(value)}</span>
            </div>
            {actualValues && <span className='actual-values-span'>{label}</span>}
          </div>
          {value2 !== undefined && (
            <div className='item-value'>
              <div className='item-name-and-percent'>
                <span>Effect:</span>
                <span className='legend-percent-span'>{formatter(value2)}</span>
              </div>
              {actualValues && <span className='actual-values-span'>{label2}</span>}
            </div>
          )}
        </div>
      ) : (
        <div className='item-value-wrapper'>
          <div className='item-value'>{formatter(value)}</div>
          {value2 !== undefined && <div className='item-value'>{formatter(value2)}</div>}
        </div>
      )}
    </>
  );

  if (!onClick) {
    return <div className={legendClassName}>{legendContents}</div>;
  }

  return (
    <InteractiveDiv className={legendClassName} onClick={onClick}>
      {legendContents}
    </InteractiveDiv>
  );
};

interface SnapshotChartWithCIProps {
  colorOverrides?: Record<string, string>;
  data: DataItem[];
  doubleBarChart?: boolean;
  dropdownOptions?: readonly DropdownOption[];
  dropdownValue?: string;
  header?: string;
  setDropdownValue?: (value: string) => void;
  setToggleLabel?: Dispatch<SetStateAction<boolean>>;
  title: string;
  toggleLabel?: boolean;
  toggleSwitchLabel?: string;
  valueFormatter?: (value: number) => string;
  additionalRightActions?: ReactNode;
  exportFormattedData?: Record<string, unknown>[];
  showDownload?: boolean;
  barChartContainerHeight?: number;
  /** whether to render confidence interval whiskers */
  showConfidenceIntervals?: boolean;
  enableTooltip?: boolean;
  pngDownloadClassName?: string;
}

export const SnapshotChartWithCI = memo(
  ({
    colorOverrides,
    data,
    doubleBarChart,
    dropdownOptions,
    dropdownValue,
    header,
    setDropdownValue,
    setToggleLabel,
    title,
    toggleLabel,
    toggleSwitchLabel,
    valueFormatter = DEFAULT_FORMATTER,
    additionalRightActions,
    exportFormattedData,
    showDownload = true,
    barChartContainerHeight = DEFAULT_BAR_CHART_CONTAINER_HEIGHT,
    showConfidenceIntervals = false,
    enableTooltip = false,
    pngDownloadClassName
  }: SnapshotChartWithCIProps) => {
    const excelDownload = useCallback(() => {
      const dataToExport = exportFormattedData || data;
      const resolvedTitle = title ? title.slice(0, 30) : 'snapshot';
      exportToExcel(dataToExport, resolvedTitle.replace(/ /g, '_'));
    }, [title, data, exportFormattedData]);

    const pngDownload = useCallback(async () => {
      await downloadPNG(pngDownloadClassName || '.snapshot-graph-contents', title);
    }, [title, pngDownloadClassName]);

    const colorMap = useMemo<Record<string, string>>(() => {
      if (R.isEmpty(data)) {
        return {};
      }
      const colorsOriginal = getColorMap(data);

      return { ...colorsOriginal, ...colorOverrides };
    }, [colorOverrides, data]);

    const renderCustomizedLabel = useCallback(
      props => {
        const { x, y, width, value } = props;
        const newWidth = Number.isNaN(width) ? 0 : width;
        const splitValue = value?.toString().split(' ');
        if (!splitValue) {
          return null;
        }
        let val1: string;
        let val2: string;
        if (typeof value === 'string') {
          val1 = `${splitValue[0]}${splitValue[1] === 'x' ? ' x' : ''}`;
          val2 = `${
            splitValue[1] === 'x' ? splitValue.slice(2).join(' ') : splitValue.slice(1).join(' ')
          }`;
        } else {
          val1 = value.top;
          val2 = value.bottom;
        }
        const showSecondLabel = !doubleBarChart || toggleLabel;

        return (
          <g>
            <text
              className='snapshot-bar-label'
              x={x + newWidth + 4}
              y={showSecondLabel ? y + 5 : y + 11}
              fill={Brand100}
              stroke={Brand100}
              strokeWidth={0}
              textAnchor='start'
              fontWeight={400}
              width={20}
              dominantBaseline='middle'
            >
              {val1}
            </text>
            {showSecondLabel && (
              <text
                className='snapshot-bar-label'
                x={x + newWidth + 4}
                y={y + 17}
                fill={Brand100}
                stroke={Brand100}
                strokeWidth={0}
                textAnchor='start'
                fontWeight={400}
                width={20}
                dominantBaseline='middle'
              >
                {val2}
              </text>
            )}
          </g>
        );
      },
      [toggleLabel, doubleBarChart]
    );

    // The legend rows (HTML) and the bars (an SVG plot that splits its own height into
    // data.length bands) line up only while both run on one pitch: the tallest label.
    const legendColumnRef = useRef<HTMLDivElement>(null);
    const [rowHeight, setRowHeight] = useState(barChartContainerHeight);

    useLayoutEffect(() => {
      const column = legendColumnRef.current;
      if (!column) {
        return undefined;
      }

      const measure = () => {
        // Measure the children, not the row: `.legend` is stretched to its track, so its
        // own height would just report back the pitch we set.
        const tallestContent = Array.from(column.children).reduce(
          (tallest, row) =>
            Array.from(row.children).reduce(
              (rowTallest, child) => Math.max(rowTallest, child.getBoundingClientRect().height),
              tallest
            ),
          0
        );
        setRowHeight(Math.max(barChartContainerHeight, Math.ceil(tallestContent)));
      };

      measure();

      // Observe the children for the same reason: the column is stretched to the height
      // this effect writes, so it cannot report a label growing (a webfont swapping in
      // after first paint). The centered children have no such feedback path.
      const observer = new ResizeObserver(measure);
      Array.from(column.children).forEach(row =>
        Array.from(row.children).forEach(child => observer.observe(child))
      );
      return () => observer.disconnect();
    }, [data, barChartContainerHeight]);

    const barContainerRef = useRef<HTMLDivElement>(null);
    const renderPortalTooltip = ({
      payload,
      label
    }: {
      payload?: readonly { payload?: DataItem & { valueLower: number; valueUpper: number } }[];
      label?: string | number;
    }) => {
      const item = payload?.[0]?.payload;
      if (!item) {
        return null;
      }

      const rect = barContainerRef.current!.getBoundingClientRect();
      const chartW = rect.width;
      const chartH = rect.height;

      // 1️⃣ X: value → pixel
      const maxValue = Math.max(...data.map(d => d.value));
      const innerW = chartW - TOOLTIP_MARGINS.left - TOOLTIP_MARGINS.right;
      const xSvg = (item.value / maxValue) * innerW + TOOLTIP_MARGINS.left;

      // 2️⃣ Y: row index → center pixel
      const idx = data.findIndex(d => d.name === item.name);
      const rowH = (chartH - (data.length - 1) * BAR_CATEGORY_GAP) / data.length;
      const ySvg = idx * (rowH + BAR_CATEGORY_GAP) + rowH / 2;

      // 3️⃣ convert to page coords
      const xPage = rect.left + window.pageXOffset + xSvg;
      const yPage = rect.top + window.pageYOffset + ySvg;

      // 4️⃣ style + clamp
      const TOOLTIP_W = 200;
      const TOOLTIP_H = 100;
      const style: CSSProperties = {
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: 9999,
        left: Math.min(xPage, window.innerWidth - TOOLTIP_W - 10),
        top: Math.max(10, yPage - TOOLTIP_H - 10)
      };

      let content: ReactNode;
      if (!label) {
        content = null;
      } else {
        const { name, value, valueLower, valueUpper } = item;
        content = (
          <div className='snapshot-tooltip' style={style}>
            <div className='header-label'>{name}</div>
            <div className='item-list'>
              {name !== 'Baseline' && (
                <div className='item-row'>
                  <div className='whiskers'>
                    <div className='line' />
                  </div>
                  <div className='name'>Confidence Interval: </div>
                  <div className='value'>
                    {`${valueFormatter(valueLower)} \u2013 ${valueFormatter(valueUpper)}`}
                  </div>
                </div>
              )}
              <div className='item-row'>
                <div className='square' style={{ backgroundColor: colorMap[name] }} />
                <div className='name'>{name === 'Baseline' ? 'Baseline: ' : 'Estimate: '}</div>
                <div className='value'>{valueFormatter(value)}</div>
              </div>
            </div>
          </div>
        );
      }
      return ReactDOM.createPortal(content, document.body);
    };
    return (
      <div className='snapshot-chart-with-ci-container'>
        <ChartContainer
          title={title}
          rightActions={
            <>
              {additionalRightActions}
              {!R.isNil(toggleLabel) && setToggleLabel && toggleSwitchLabel && (
                <ToggleSwitch
                  label={toggleSwitchLabel}
                  checked={toggleLabel}
                  onChange={setToggleLabel}
                />
              )}
              {dropdownOptions && dropdownValue && setDropdownValue && (
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  value={dropdownValue}
                  options={dropdownOptions}
                  onChange={setDropdownValue}
                />
              )}
              {showDownload && <DownloadDropdown onClickOptions={[excelDownload, pngDownload]} />}
            </>
          }
        >
          <div id='snapshot-graph-contents' className='snapshot-graph-contents'>
            {header && <div className='snapshot-graph-header'>{header}</div>}
            <div className='bar-charts-container' style={{ height: data.length * rowHeight }}>
              <div className='left-of-chart' ref={legendColumnRef}>
                {data.map(item => (
                  <Legend
                    name={item.name}
                    value={item.value}
                    value2={item.value2}
                    computedValue={item.computedValue}
                    formatter={valueFormatter}
                    key={item.name}
                    incrementality={item.incrementality}
                  />
                ))}
              </div>
              <div className='snapshot-bar-container' ref={barContainerRef}>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart
                    barCategoryGap={BAR_CATEGORY_GAP}
                    data={data}
                    layout='vertical'
                    margin={{ top: 0, right: 50, bottom: 0, left: 0 }}
                  >
                    <ChartXAxis type='number' domain={[0, 'dataMax']} hide />
                    <ChartYAxis type='category' dataKey='name' hide />
                    <Bar
                      dataKey='value'
                      isAnimationActive={false}
                      opacity={doubleBarChart ? 0.5 : 1}
                      strokeWidth={2}
                      radius={title === 'Spend and Effect Share' ? 0 : 2}
                      strokeOpacity={1}
                    >
                      {data.map(item => (
                        <Cell key={`${item.name}value1`} fill={colorMap[item.name]} />
                      ))}
                      {showConfidenceIntervals && (
                        <ErrorBar dataKey='errorX' strokeWidth={2} stroke={Brand100} />
                      )}
                      {(toggleLabel || doubleBarChart) && (
                        <LabelList dataKey='label' content={renderCustomizedLabel} />
                      )}
                    </Bar>
                    {enableTooltip && (
                      <Tooltip
                        content={renderPortalTooltip}
                        isAnimationActive={false}
                        cursor={{ fill: 'none' }}
                      />
                    )}
                    {doubleBarChart && (
                      <Bar dataKey='value2' isAnimationActive={false}>
                        {data.map(item => (
                          <Cell key={`${item.name}value2`} fill={colorMap[item.name]} />
                        ))}
                        {(toggleLabel || doubleBarChart) && (
                          <LabelList dataKey='label2' content={renderCustomizedLabel} />
                        )}
                      </Bar>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </ChartContainer>
      </div>
    );
  }
);
